import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type MouseEvent,
} from "react";
import { ArrowUp, X } from "lucide-react";
import { useTranslation } from "@ferrivia/i18n";
import type { ReplyPreview } from "@ferrivia/api-contract";
import s from "./Composer.module.css";

export interface ComposerProps {
  placeholder: string;
  onSend: (text: string) => void;
  sendLabel?: string;
  className?: string;
  replyTo?: ReplyPreview | null;
  onClearReply?: () => void;
  renderAccessory?: (draft: { text: string; clear: () => void }) => ReactNode;
}

function replySnippet(reply: ReplyPreview): string {
  if (reply.redactedAt) return "";
  const first = reply.body.blocks[0];
  if (first && "text" in first && typeof first.text === "string") {
    return first.text.length > 80 ? first.text.slice(0, 80) + "…" : first.text;
  }
  return "";
}

export interface ComposerHandle {
  /** Focus the text input. Call this synchronously from inside a tap or swipe handler — a mobile browser only raises its keyboard for a focus() made during a user gesture. */
  focus: () => void;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  { placeholder, onSend, sendLabel, className, replyTo, onClearReply, renderAccessory },
  handleRef,
) {
  const { t } = useTranslation("chat");
  const resolvedSendLabel = sendLabel ?? t("composer.send", "Lähetä");
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // `preventScroll` for the same reason as handleInputMouseDown below.
  useImperativeHandle(handleRef, () => ({
    focus: () => inputRef.current?.focus({ preventScroll: true }),
  }), []);

  // Picking a reply target is the start of typing one, so the composer takes
  // focus rather than costing a second tap. Keyed on the target's id, not the
  // `replyTo` object — callers rebuild that object every render.
  //
  // This effect runs a render after the tap that picked the target, which is
  // too late for iOS to treat the focus as gesture-driven and raise the
  // keyboard. It is the fallback; a caller that wants the keyboard focuses
  // through the handle above, from inside the gesture itself.
  const replyToId = replyTo?.id ?? null;
  useEffect(() => {
    if (replyToId) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [replyToId]);

  function submit() {
    const text = value.trim();
    if (!text) {
      return;
    }
    onSend(text);
    setValue("");
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      submit();
    }
  }

  function handleInputMouseDown(event: MouseEvent<HTMLInputElement>) {
    if (!window.matchMedia?.("(pointer: coarse)").matches) {
      return;
    }
    event.currentTarget.focus({ preventScroll: true });
    event.preventDefault();
  }

  const classes = className ? `${s.root} ${className}` : s.root;

  return (
    <div className={classes}>
      {replyTo ? (
        <div className={s.replyBar}>
          <div className={s.replyBarContent}>
            <span className={s.replyBarName}>
              {t("reply.replyingTo", {
                name: replyTo.authorName ?? t("reply.unknownSender", "Tuntematon lähettäjä"),
                defaultValue: "Vastaus käyttäjälle {{name}}",
              })}
            </span>
            <span className={s.replyBarText}>{replySnippet(replyTo)}</span>
          </div>
          <button
            type="button"
            className={s.replyBarClose}
            onClick={onClearReply}
            aria-label={t("reply.dismiss", "Peruuta vastaus")}
          >
            <X aria-hidden size={16} />
          </button>
        </div>
      ) : null}
      <div className={s.row}>
        <input
          ref={inputRef}
          className={s.input}
          type="search"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          onMouseDown={handleInputMouseDown}
          placeholder={placeholder}
          autoComplete="off"
        />
        {renderAccessory?.({ text: value, clear: () => setValue("") })}
        <button
          type="button"
          className={s.send}
          onMouseDown={(event) => event.preventDefault()}
          onClick={submit}
          aria-label={resolvedSendLabel}
        >
          <ArrowUp aria-hidden size={18} />
        </button>
      </div>
    </div>
  );
});
